import {
  useTheme,
  type ModalProps,
  Drawer as ChakraDrawer,
} from '@chakra-ui/react-v2';
import { useState, useEffect, useMemo } from 'react';
import type { Merge } from 'type-fest';

import { noop } from '@frontend/utils';

import { DrawerContextProvider, useDrawerContext } from './DrawerContext';
import { DEFAULT_PROPS } from './const';
import { DrawerSize } from './types';

export interface DrawerProps
  extends Merge<Omit<ModalProps, 'onClose'>, { size?: DrawerSize }> {
  onClose?: ModalProps['onClose'];
}

/**
 * This multi-layer drawer implementation is modeled after Chakra's single-layer
 * drawer implementation.
 *
 * This multi-layer drawer can currently only display on the right.
 *
 * https://github.com/chakra-ui/chakra-ui/blob/main/packages/modal/src/drawer.tsx
 */
export const Drawer = ({
  isOpen,
  onClose = noop,
  children,
  size,
  ...restProps
}: DrawerProps) => {
  const theme = useTheme();
  const drawerStyleConfig = theme.components?.Drawer;

  // Multi-layer drawer push/pull context
  const [level, setLevel] = useState(0);
  const { operations: parentDrawerOperations } = useDrawerContext() || {};

  useEffect(() => {
    if (parentDrawerOperations) {
      if (isOpen) {
        // Issue a push command to the parent when a child opens
        parentDrawerOperations.push();

        return () => {
          // Issue the matching pull command when the child closes
          parentDrawerOperations.pull();
        };
      }
    }
  }, [isOpen, parentDrawerOperations]);

  const operations = useMemo(
    () => ({
      push: () => {
        setLevel((currentLevel) => currentLevel + 1);
        if (parentDrawerOperations) {
          parentDrawerOperations.push();
        }
      },
      pull: () => {
        setLevel((currentLevel) => currentLevel - 1);
        if (parentDrawerOperations) {
          parentDrawerOperations.pull();
        }
      },
    }),
    [parentDrawerOperations],
  );

  return (
    <DrawerContextProvider
      value={{
        level,
        operations,
        size: size ?? DEFAULT_PROPS.size,
      }}
    >
      <ChakraDrawer
        isOpen={isOpen}
        onClose={onClose}
        styleConfig={drawerStyleConfig}
        size={size}
        {...restProps}
      >
        {children}
      </ChakraDrawer>
    </DrawerContextProvider>
  );
};
